import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IndianRupee, ClipboardList, Package, Users, AlertTriangle, Clock, Loader2 } from "lucide-react";
import { api, fmt, fmtDate } from "@/lib/api";
import { StatusChip } from "@/pages/OrdersPage";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setError("Couldn't load admin stats."));
  }, []);

  if (error) return <p className="rounded-xl border border-red-500/25 bg-red-500/[0.06] p-6 text-sm text-red-400" data-testid="admin-stats-error">{error}</p>;
  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;

  const cards = [
    { icon: IndianRupee, label: "Revenue", value: fmt(stats.revenue), tone: "text-amber-500" },
    { icon: ClipboardList, label: "Orders", value: stats.orders, tone: "text-white" },
    { icon: Package, label: "Products", value: stats.products, tone: "text-white" },
    { icon: Users, label: "Customers", value: stats.customers, tone: "text-white" },
    { icon: AlertTriangle, label: "Low Stock", value: stats.low_stock, tone: stats.low_stock ? "text-amber-400" : "text-emerald-400" },
    { icon: Clock, label: "Pending Orders", value: stats.pending_orders, tone: stats.pending_orders ? "text-amber-400" : "text-emerald-400" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] p-5" data-testid={`admin-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <c.icon className={`h-5 w-5 ${c.tone}`} />
            <p className="font-display mt-3 text-2xl font-semibold tracking-tight text-white">{c.value}</p>
            <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">{c.label}</p>
          </div>
        ))}
      </div>

      <h3 className="font-display mb-4 mt-10 text-xl font-medium tracking-tight">Recent Orders</h3>
      <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#FFFEFA]">
        <table className="w-full min-w-[640px] text-left text-sm" data-testid="admin-recent-orders">
          <thead>
            <tr className="border-b border-white/[0.07] text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              <th className="px-5 py-4">Order</th>
              <th className="px-5 py-4">Customer</th>
              <th className="px-5 py-4">Total</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Date</th>
            </tr>
          </thead>
          <tbody>
            {stats.recent_orders.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-neutral-500">No orders yet.</td></tr>
            )}
            {stats.recent_orders.map((o) => (
              <tr key={o.id} className="cursor-pointer border-b border-white/[0.04] transition-colors hover:bg-white/[0.03]" onClick={() => navigate("/admin/orders")}>
                <td className="px-5 py-4 font-mono font-bold text-amber-400">{o.order_number}</td>
                <td className="px-5 py-4 text-neutral-300">{o.contact?.name}</td>
                <td className="px-5 py-4 font-semibold text-white">{fmt(o.total)}</td>
                <td className="px-5 py-4"><StatusChip status={o.status} /></td>
                <td className="px-5 py-4 text-neutral-500">{fmtDate(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
